"use client";

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react";
import { getMarkRange } from "@tiptap/core";
import { TextSelection } from "@tiptap/pm/state";
import { BubbleMenu } from "@tiptap/react/menus";
import { useEditorState, type Editor } from "@tiptap/react";

import { Button } from "@/components/ui/button";
import { Icon, type IconName } from "@/components/ui/icon";
import { Input } from "@/components/ui/input";
import { showToast } from "@/components/ui/sonner";
import { cn } from "@/lib/utils";

import { normalizeLinkUrl, prettyLinkLabel } from "./link-utils";

/**
 * Floating selection toolbar for the step content editor.
 *
 * - Highlight text → a toolbar hovers above the selection with Bold / Italic /
 *   Underline and a Link action.
 * - Click "Link" → an inline editor lets you set the visible text + URL using
 *   our own Input + button styles.
 * - Put the cursor in an existing link → the toolbar shows the URL with Open,
 *   Edit, and Remove. (Plain clicks don't navigate; Cmd/Ctrl-click does — see
 *   the editor's `handleClick`.)
 */
export function EditorFloatingToolbar({
  editor,
  boundaryRef,
}: {
  editor: Editor;
  /** The editor's own container — the menu mounts here (see appendTo note). */
  boundaryRef?: RefObject<HTMLElement | null>;
}) {
  const [editing, setEditing] = useState(false);
  // Explicit dismissal — see the outside-pointerdown effect below. Used to
  // hide the menu when `appendTo: document.body` makes the BubbleMenu's own
  // blur handler unreliable.
  const [dismissed, setDismissed] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const snapshot = useEditorState({
    editor,
    selector: (ctx) => {
      const e = ctx.editor;
      const { from, to } = e.state.selection;
      return {
        bold: e.isActive("bold"),
        italic: e.isActive("italic"),
        underline: e.isActive("underline"),
        isLink: e.isActive("link"),
        linkHref: (e.getAttributes("link").href as string | undefined) ?? "",
        from,
        to,
      };
    },
  });

  const flags = snapshot ?? {
    bold: false,
    italic: false,
    underline: false,
    isLink: false,
    linkHref: "",
    from: 0,
    to: 0,
  };

  // Leaving the current selection/link cancels an in-progress edit so the
  // toolbar never re-opens stale in the wrong place.
  const selKey = `${flags.from}:${flags.to}`;
  useEffect(() => {
    setEditing(false);
    // A new selection un-dismisses the toolbar so it can show again.
    setDismissed(false);
  }, [selKey]);

  // Dismiss on a click outside the editor AND outside the menu itself. The
  // menu mounts in `document.body` (so its size never disturbs the sheet
  // layout — see appendTo), which defeats the BubbleMenu's built-in
  // blur-to-hide, so we drive it explicitly. Flipping `dismissed` changes the
  // memoized `shouldShow` identity, which makes the React BubbleMenu push new
  // options to the plugin and re-evaluate visibility (so it hides at once).
  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      const t = event.target as Node | null;
      if (!t) return;
      if (boundaryRef?.current?.contains(t)) return; // editor + its toolbars
      if (menuRef.current?.contains(t)) return; // the floating menu / link form
      setDismissed(true);
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () =>
      document.removeEventListener("pointerdown", onPointerDown, true);
  }, [boundaryRef]);

  // Cmd-K (Mac) / Ctrl-K (Windows) opens the link editor for the current
  // selection — the same affordance real editors use.
  useEffect(() => {
    const dom = editor.view.dom;
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== "k") {
        return;
      }
      if (!editor.isEditable) return;
      const { selection } = editor.state;
      if (selection.empty && !editor.isActive("link")) return;
      event.preventDefault();
      setEditing(true);
    };
    dom.addEventListener("keydown", onKeyDown);
    return () => dom.removeEventListener("keydown", onKeyDown);
  }, [editor]);

  const shouldShow = useCallback(
    ({ state }: { state: typeof editor.state }) => {
      if (!editor.isEditable) return false;
      if (dismissed) return false;
      if (editor.isActive("link")) return true;
      const { selection } = state;
      // Only for real text selections (a node selection — e.g. clicking a
      // variable chip — has its own popover and shouldn't get this toolbar).
      if (!(selection instanceof TextSelection)) return false;
      return !selection.empty;
    },
    [editor, dismissed],
  );

  // Stable identity so the BubbleMenu doesn't re-push options every render.
  // `fixed` + body mount = viewport-correct positioning inside the
  // transformed sheet, with no document overflow (so the sheet never shifts).
  const floatingOptions = useMemo(
    () => ({
      strategy: "fixed" as const,
      placement: "top" as const,
      offset: 8,
      flip: true,
      // Keep an 8px gap from the viewport edges so the popup never butts up
      // against the side of the screen.
      shift: { padding: 8 },
    }),
    [],
  );

  const openHref = useCallback((href: string) => {
    if (href) window.open(href, "_blank", "noopener,noreferrer");
  }, []);

  const removeLink = useCallback(() => {
    editor.chain().focus().extendMarkRange("link").unsetLink().run();
    setEditing(false);
    showToast.success("Link removed");
  }, [editor]);

  return (
    <BubbleMenu
      ref={menuRef}
      editor={editor}
      pluginKey="stepEditorFloatingToolbar"
      shouldShow={shouldShow}
      // Mount in document.body with `position: fixed` (see floatingOptions).
      // The sheet is transform-animated, so a `fixed` element placed INSIDE it
      // is positioned relative to the sheet and lands off-screen; mounting in
      // body (no transform) makes floating-UI's viewport math correct. Because
      // the element is fixed and lives in body, its size can never extend a
      // scroll area or push the sheet sideways — `flip`/`shift` just keep it on
      // screen above the selection. Body defeats the built-in blur-to-hide, so
      // the outside-pointerdown effect above handles dismissal.
      appendTo={() => document.body}
      options={floatingOptions}
      className="z-[60]"
    >
      <div className="flex items-center overflow-hidden rounded-lg border border-border bg-surface-elevated shadow-[0px_8px_24px_0px_rgba(0,0,0,0.35)]">
        {editing ? (
          <LinkEditorForm editor={editor} onClose={() => setEditing(false)} />
        ) : flags.isLink ? (
          <div className="flex items-center gap-1 px-1.5 py-1">
            <button
              type="button"
              onClick={() => openHref(flags.linkHref)}
              title={flags.linkHref}
              className="flex max-w-[200px] items-center gap-1.5 rounded-md px-2 py-1 text-xs text-[#86b3ff] transition-colors hover:bg-contrast/6"
            >
              <Icon name="IconChainLink3" size={14} className="shrink-0" />
              <span className="truncate underline">
                {prettyLinkLabel(flags.linkHref)}
              </span>
            </button>
            <ToolbarDivider />
            <FloatingButton
              icon="IconArrowUpRight"
              label="Open link"
              onClick={() => openHref(flags.linkHref)}
            />
            <FloatingButton
              icon="IconPencilLine"
              label="Edit link"
              onClick={() => setEditing(true)}
            />
            <FloatingButton
              icon="IconTrashCanSimple"
              label="Remove link"
              destructive
              onClick={removeLink}
            />
          </div>
        ) : (
          <div className="flex items-center gap-0.5 px-1.5 py-1">
            <FloatingButton
              icon="IconBold"
              label="Bold"
              isActive={flags.bold}
              onClick={() => editor.chain().focus().toggleBold().run()}
            />
            <FloatingButton
              icon="IconItalic"
              label="Italic"
              isActive={flags.italic}
              onClick={() => editor.chain().focus().toggleItalic().run()}
            />
            <FloatingButton
              icon="IconUnderline"
              label="Underline"
              isActive={flags.underline}
              onClick={() => editor.chain().focus().toggleUnderline().run()}
            />
            <ToolbarDivider />
            <FloatingButton
              icon="IconChainLink3"
              label="Add link"
              onClick={() => setEditing(true)}
            />
          </div>
        )}
      </div>
    </BubbleMenu>
  );
}

function ToolbarDivider() {
  return <span aria-hidden className="mx-1 h-4 w-px bg-contrast/10" />;
}

interface FloatingButtonProps {
  icon: IconName;
  label: string;
  isActive?: boolean;
  destructive?: boolean;
  onClick: () => void;
}

function FloatingButton({
  icon,
  label,
  isActive,
  destructive,
  onClick,
}: FloatingButtonProps) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={isActive}
      title={label}
      onClick={onClick}
      className={cn(
        "group flex size-7 cursor-pointer items-center justify-center rounded-md transition-all",
        destructive
          ? "text-muted-foreground hover:text-destructive"
          : isActive
            ? "bg-contrast/[0.10]"
            : "hover:bg-contrast/6",
      )}
    >
      <Icon
        name={icon}
        size={16}
        className={cn(
          "transition-opacity",
          destructive
            ? "text-current opacity-100"
            : isActive
              ? "text-heading opacity-100"
              : "text-heading opacity-60 group-hover:opacity-90",
        )}
      />
    </button>
  );
}

/** Returns the editable text for the active link, or the selected text. */
function getActiveLinkText(editor: Editor): string {
  const { state } = editor;
  const { selection } = state;
  if (editor.isActive("link")) {
    const range = getMarkRange(
      state.doc.resolve(selection.from),
      state.schema.marks.link,
    );
    if (range) return state.doc.textBetween(range.from, range.to, "");
  }
  return state.doc.textBetween(selection.from, selection.to, "");
}

export function LinkEditorForm({
  editor,
  onClose,
}: {
  editor: Editor;
  onClose: () => void;
}) {
  const isEdit = editor.isActive("link");
  const [text, setText] = useState(() => getActiveLinkText(editor));
  const [url, setUrl] = useState(() =>
    isEdit ? ((editor.getAttributes("link").href as string) ?? "") : "",
  );
  const [error, setError] = useState(false);
  const urlRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    urlRef.current?.focus();
    urlRef.current?.select();
  }, []);

  const submit = useCallback(() => {
    const href = normalizeLinkUrl(url);
    if (!href) {
      setError(true);
      return;
    }

    // Resolve the range we're about to link: the whole link mark when editing,
    // otherwise the current selection.
    const { state } = editor;
    let from = state.selection.from;
    let to = state.selection.to;
    if (isEdit) {
      const range = getMarkRange(
        state.doc.resolve(state.selection.from),
        state.schema.marks.link,
      );
      if (range) {
        from = range.from;
        to = range.to;
      }
    }

    // Does the range contain inline atom chips (variable mentions / AI prompts)?
    // If so we must apply the link as a MARK and keep those nodes intact —
    // replacing the range with a plain text node would delete the chips.
    let hasChip = false;
    let plainText = "";
    state.doc.nodesBetween(from, to, (n) => {
      if (n.isText) plainText += n.text ?? "";
      else if (n.isInline && !n.isText) hasChip = true;
      return true;
    });

    const label = text.trim();
    const chain = editor.chain().focus();
    if (isEdit) chain.extendMarkRange("link");

    if (!hasChip && label && label !== plainText) {
      // Pure-text selection and the user changed the display text → rewrite it
      // as a labeled link.
      chain.insertContent({
        type: "text",
        text: label,
        marks: [{ type: "link", attrs: { href } }],
      });
    } else {
      // Preserve the selected content (variables / prompts / formatting) and
      // just apply (or update) the link mark over it.
      chain.setLink({ href });
    }
    chain.run();
    onClose();
    showToast.success(isEdit ? "Link updated" : "Link added");
  }, [editor, url, text, isEdit, onClose]);

  const removeLink = useCallback(() => {
    editor.chain().focus().extendMarkRange("link").unsetLink().run();
    onClose();
    showToast.success("Link removed");
  }, [editor, onClose]);

  return (
    <div
      className="flex w-[340px] flex-col gap-3 p-3"
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          onClose();
        }
      }}
    >
      <div className="flex flex-col gap-1.5">
        <label
          htmlFor="link-text"
          className="flex items-center gap-1.5 text-xs font-medium text-contrast/60"
        >
          <Icon name="IconTextSize" size={13} />
          Text
        </label>
        <Input
          id="link-text"
          variant="modal"
          size="sm"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Text to display"
          aria-label="Link text"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              urlRef.current?.focus();
            }
          }}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <label
          htmlFor="link-url"
          className="flex items-center gap-1.5 text-xs font-medium text-contrast/60"
        >
          <Icon name="IconChainLink3" size={13} />
          Link
        </label>
        <Input
          id="link-url"
          ref={urlRef}
          variant="modal"
          size="sm"
          value={url}
          onChange={(e) => {
            setUrl(e.target.value);
            setError(false);
          }}
          placeholder="Paste or type a link"
          aria-label="Link URL"
          aria-invalid={error}
          className={cn(error && "!border-destructive")}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submit();
            }
          }}
        />
      </div>
      <div className="flex items-center justify-between gap-2">
        {isEdit ? (
          <button
            type="button"
            onClick={removeLink}
            aria-label="Remove link"
            title="Remove link"
            className="flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:text-destructive"
          >
            <Icon name="IconTrashCanSimple" size={16} />
          </button>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={submit}
            disabled={!url.trim()}
            icon={<Icon name="IconChainLink3" size={14} />}
          >
            {isEdit ? "Save" : "Add link"}
          </Button>
        </div>
      </div>
    </div>
  );
}
